import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { ActivityGridViewData, Column, ColumnKeys, INVESTIGATION_ACTIVITIES, PREDEFINED_FILTERS_KEYS } from '../types';
import { WEB_COMPONENT_GRID_VIEW } from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import { ActivityGridDataProvider } from '../dataProvider/ActivityGridDataProvider';
import {
  fileNameRenderer,
  statusRenderer,
  activityNameRenderer,
  startedOnRenderer,
  finishedOnRenderer,
  fileExpirationDateRenderer,
  actionsRenderer,
  formatDate,
} from './customGridRenderers';
import { TemplateState } from '../../../types/common/types';
import { GzDialogWebComponent } from '../../../ui/gzDialog';

export class ActivityItemGridView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$deleteAllActivitiesBtn = this.shadow.querySelector('#deleteAllActivitiesBtn');
    this.dom.$tooltipDeleteAllActivitiesBtn = this.shadow.querySelector('#tooltip-deleteAllActivitiesBtn');
    this.dom.$allInvFilesAreDisplayedHereText = this.shadow.querySelector('#all-inv-files-are-displayed-here');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>;

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }

    this.setTranslatedStrings();
  }

  public setTranslatedStrings() {
    this.dom.$deleteAllActivitiesBtn.innerHTML = this.dependencies.translateFn('Delete all');
    this.dom.$allInvFilesAreDisplayedHereText.innerText = this.dependencies.translateFn(
      'All available investigation files are displayed here'
    );
  }

  buildSuccessView(data: ActivityGridViewData) {
    this.resetTemplateState(this.dom.$wrapper);

    if (!data) {
      this.buildErrorView();
      return;
    }

    this.buildGridView(data);
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private buildGridView(data: ActivityGridViewData) {
    const view = data.views[0];

    this.dom.$gridView.dataProvider = new ActivityGridDataProvider(this.dependencies);

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.FILE_NAME]: fileNameRenderer.bind(this),
      [ColumnKeys.STATUS]: statusRenderer.bind(this),
      [ColumnKeys.ACTIVITY]: activityNameRenderer.bind(this),
      [ColumnKeys.STARTED_ON]: startedOnRenderer.bind(this),
      [ColumnKeys.FINISHED_ON]: finishedOnRenderer.bind(this),
      [ColumnKeys.FILE_EXPIRES_ON]: fileExpirationDateRenderer.bind(this),
      [ColumnKeys.ACTION]: actionsRenderer.bind(this),
    };

    this.dom.$gridView.viewId = view.id;
    this.dom.$gridView.allColumnDetails = data.allColumns;
    this.dom.$gridView.columns = view.displayedColumns;
    this.dom.$gridView.filters = view.filters;
    this.dom.$gridView.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$gridView.defaultViewProps = data.defaultViewProps;
    this.dom.$gridView.pageSize = view.pageSize;
    this.dom.$gridView.isCompactViewChecked = true;
    this.dom.$gridView.removeFilters = true;
    this.dom.$gridView.hideGridRefresh = true;
    this.dom.$gridView.hideFiltersToggle = true;
    this.dom.$gridView.disableGridSettings = true;
    this.dom.$gridView.hideHeaderActions = false;
    this.dom.$gridView.hideFooter = false;
    this.dom.$gridView.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No activity available'),
    };
    this.dom.$gridView.showGlobalActions = true;
    this.dom.$gridView.showConfigurableArea = true;
    this.dom.$gridView.propertiesSet = true;
  }

  public onCancelItem(event) {
    const itemId = event.target.getAttribute('data-internal-id');
    const itemType = Number(event.target.getAttribute('data-item-type'));
    const windowBodyHtml = `<div id="window-wrapper" style="margin-top: -18px">
          <gz-title class="mb-4" fonSize="18" fontWeight="500">
             ${this.dependencies.translateFn('Cancel Activity?')}
          </gz-title>
          <gz-text fontSize="14" class="mb-2">
              ${this.dependencies.translateFn(
                `Once canceled, the data collected in this archive will be discarded. This action cannot be undone.`
              )}
           </gz-text>
     </div>`;

    const windowFooterHtml = `<gz-button id='confirm' type='full' color='primary' class='mr-2'>
       ${this.dependencies.translateFn('CANCEL ACTIVITY')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CLOSE')}
      </gz-button>`;
    const windowTitle = '';
    const cancelCallback = () => {};
    const confirmCallback = () => {
      this.dom.$gridView.dataProvider.cancelActivityItem(itemId, itemType);
    };

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      cancelCallback,
      confirmCallback
    );
  }

  public async onSeeDetailsItem(event) {
    const activityId = event.target.getAttribute('data-internal-id');
    const errorDetails = await this.dom.$gridView.dataProvider.getActivityDetails(activityId);

    if (!errorDetails.endDate || !errorDetails.errorMessage || !errorDetails.activityType) {
      return;
    }

    let endDate,
      errorMessage = '';
    if (errorDetails.endDate) {
      endDate = formatDate(errorDetails.endDate);
    }

    if (errorDetails.errorMessage) {
      errorMessage = errorDetails.errorMessage;
    }

    let activityErrorDetails;
    switch (errorDetails.activityType) {
      case INVESTIGATION_ACTIVITIES.COLLECT_INVESTIGATION_PACKAGE:
        activityErrorDetails = this.dependencies.translateFn('Investigation package collection failed on {endDate}', {
          endDate: endDate,
        });
        break;
      case INVESTIGATION_ACTIVITIES.REMOTE_SHELL_DOWNLOAD:
        activityErrorDetails = this.dependencies.translateFn('Remote Shell download failed on {endDate}', {
          endDate: endDate,
        });
        break;
      default:
        activityErrorDetails = this.dependencies.translateFn('N/A');
        break;
    }

    const windowBodyHtml = `
        <div id="window-wrapper" style="margin-top: -18px">
          <gz-title class="mb-4" fonSize="18" fontWeight="500">
                ${this.dependencies.translateFn('Investigation Files Activity Details')}
          </gz-title>
          <gz-text fontSize="14" class="mb-2">
                ${activityErrorDetails}
          </gz-text>
          <gz-text fontSize="14" class="mb-2">
                ${this.dependencies.translateFn('Failure reason: {errorMessage}', {
                  errorMessage: errorMessage,
                })}
          </gz-text>
        </div>`;

    const windowFooterHtml = `<gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CLOSE')}
      </gz-button>`;
    const windowTitle = '';
    const closeCallback = () => {};

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, closeCallback);
  }

  public onDownloadFileItem(event) {
    const activityId = event.target.getAttribute('data-internal-id');
    this.dom.$gridView.dataProvider.downloadInvestigationFileActivity(activityId);
  }

  public onRetrieveFileItem(event): void {
    const activityId = event.target.getAttribute('data-internal-id');
    this.dom.$gridView.dataProvider.retrieveInvestigationFileActivity(activityId);
  }

  public showDeleteAllConfirmationDialog() {
    const windowBodyHtml = `<div id="window-wrapper" style="margin-top: -18px">
          <gz-title class="mb-4" fonSize="18" fontWeight="500">
             ${this.dependencies.translateFn('Delete all investigation files?')}
          </gz-title>
          <gz-text fontSize="14" class="mb-2">
              ${this.dependencies.translateFn(
                `Investigation files are automatically deleted 24 hours after collection.`
              )}
           </gz-text>
          <gz-text fontSize="14" class="mb-2">
              ${this.dependencies.translateFn('Do you want to manually delete all files now?')}
          </gz-text>
     </div>`;

    const windowFooterHtml = `<gz-button id='confirm' type='full' color='primary' class='mr-2'>
       ${this.dependencies.translateFn('DELETE ALL')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CANCEL')}
      </gz-button>`;
    const windowTitle = '';
    const cancelCallback = () => {};
    const confirmCallback = () => {
      this.dom.$gridView.dataProvider.deleteAllActivities();
    };

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      cancelCallback,
      confirmCallback
    );
  }

  public disableDeleteAllActivitiesBtn(disable: boolean) {
    this.dom.$deleteAllActivitiesBtn.disabled = disable;
  }

  public setTooltipForDeleteAllActivitiesBtn(tooltip: string = '') {
    this.dom.$tooltipDeleteAllActivitiesBtn.tooltipText = tooltip;

    if (this.dependencies.userWithout2FA) {
      this.dom.$tooltipDeleteAllActivitiesBtn.tooltipText = this.dependencies.translateFn(
        'Enable 2FA on your account, to activate forensic data collection.'
      );
      this.disableDeleteAllActivitiesBtn(true);
    }
  }
}
